import { Button, buttonFunctions } from "~/hec/components/cta";
import { cn } from "~/lib/utils";

import type { FeatureLinkProps, FeatureLinkTarget } from "./types";

const hasDestination = (target?: FeatureLinkTarget) => {
  if (!target || target.clickFunction === buttonFunctions.nothing) return false;
  if (target.clickFunction === buttonFunctions.linkToElement) {
    return !!target.scrollTargetId?.trim();
  }
  return !!target.url;
};

/**
 * Molecule: wraps a whole card or hero in the shared CTA link. It renders the
 * target it is given; the organism passes it through `useFeatureLinkTarget`
 * first, which sends an internal URL on an SFRA-owned path as a full page load.
 * Renders a plain `<div>` when there is nowhere to go, never a link to "/"
 * (what `appendSearchParams` returns for an empty URL).
 */
export function FeatureLink({
  target,
  className,
  children,
  "data-testid": dataTestId,
  ...rest
}: FeatureLinkProps) {
  if (!target || !hasDestination(target)) {
    return (
      <div className={className} {...rest} data-testid={dataTestId}>
        {children}
      </div>
    );
  }

  return (
    <Button
      clickFunction={target.clickFunction}
      routeType={target.routeType}
      url={target.url}
      scrollTargetId={target.scrollTargetId?.trim()}
      openInNewTab={target.openInNewTab}
      isRevShare={target.isRevShare}
      // Reset the link variant's text color; the card's own text sets it.
      className={cn("text-inherit", className)}
      dataTestId={dataTestId}
      {...rest}
    >
      {children}
    </Button>
  );
}
